import { PropsWithChildren, ReactNode } from "react"
import { Stack } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { Box, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconProp, SizeProp } from "@fortawesome/fontawesome-svg-core"
import { StandardChip } from "../../../shared/components/badges/StandardChip"

const useStyles = makeStyles()((theme) => ({
    box: {
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        borderRadius: ".75rem",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
        background: "white",
        padding: "2rem",
    },
    titleColor: {
        color: theme.palette.tailwind.slate[600],
    },
    textColor: {
        color: theme.palette.tailwind.slate[900],
    },
    iconColor: {
        color: theme.palette.tailwind.sky[900],
    },
    chip: {
        height: "auto",
        fontWeight: 500,
    },
    listItem: {
        display: "flex",
        justifyContent: "space-between",
        gap: "1rem",
    },
    listItemRight: {
        textAlign: "right",
    },
}))

type CardWithIconProps = Readonly<
    PropsWithChildren<{
        title: JSX.Element
        icon: IconProp
        iconSize?: SizeProp
    }>
>

export const CardWithIcon = ({ title, children, icon, iconSize }: CardWithIconProps) => {
    const { classes } = useStyles()
    return (
        <Stack direction="row" justifyContent="space-between" spacing="1.5rem">
            <CardContentBox title={title}>{children}</CardContentBox>
            <FontAwesomeIcon icon={icon} size={iconSize} className={classes.iconColor} />
        </Stack>
    )
}
type CardContentProps = Readonly<
    PropsWithChildren<{
        title?: JSX.Element
        listSpacing?: string
        wrapSpacing?: string
    }>
>

export const CardContentBox = ({ title, children, wrapSpacing = "1.5rem", listSpacing = "1rem" }: CardContentProps) => {
    const { classes } = useStyles()
    return (
        <Stack direction="column" spacing={wrapSpacing}>
            {title && (
                <Typography variant="body1" fontWeight={600} className={classes.titleColor}>
                    {title}
                </Typography>
            )}
            <Stack direction="column" spacing={listSpacing}>
                {children}
            </Stack>
        </Stack>
    )
}

type CardListItemProps = Readonly<{
    title: JSX.Element
    content?: ReactNode
    chipTitle?: string
    isOneLineContent?: boolean
}>
export const CardListItem = ({ title, content, chipTitle, isOneLineContent = false }: CardListItemProps) => {
    const { classes, cx } = useStyles()
    return (
        <Box className={cx(isOneLineContent && classes.listItem)}>
            <Typography variant="body1" className={classes.titleColor}>
                {title}
            </Typography>
            <Box className={cx(isOneLineContent && classes.listItemRight)}>
                <Typography variant="subtitle1" className={classes.textColor}>
                    {content ?? "-"}
                </Typography>
                {chipTitle && (
                    <StandardChip className={classes.chip} variant="grey" size="small">
                        {chipTitle}
                    </StandardChip>
                )}
            </Box>
        </Box>
    )
}
